import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { money } from "@/lib/format";
import { ChaseSheet } from "@/components/ChaseSheet";
import { BottomSheet } from "@/components/BottomSheet";
import { Radar, TriangleAlert, MessageSquare, ChevronRight, CheckCircle2, Loader2, Eye, Trash2 } from "lucide-react";
import { ConfirmSheet } from "@/components/ConfirmSheet";

const statusLabel = (a) => {
  if (a.overdue) return { text: `${a.days_overdue} day${a.days_overdue === 1 ? "" : "s"} overdue`, tone: "over" };
  if (a.no_due_date) return { text: "No due date · Payment not recorded", tone: "out" };
  return { text: "Outstanding · Payment not recorded", tone: "out" };
};

const Row = ({ item, onChase, onOpen }) => {
  const isInvoice = item.kind === "invoice";
  const s = isInvoice ? statusLabel(item) : { text: `Awaiting reply · ${item.age_days} day${item.age_days === 1 ? "" : "s"}`, tone: item.possible_missed_invoice ? "over" : "quote" };
  const toneClass = s.tone === "over" ? "text-error" : s.tone === "quote" ? "text-brandSecondary" : "text-muted-foreground";
  return (
    <div data-testid={`radar-item-${item.doc_id}`} className="bg-surface border border-border rounded-[14px] p-3.5 flex items-center gap-3">
      <button onClick={() => onOpen(item)} className="flex-1 min-w-0 text-left active:opacity-70 transition-opacity" data-testid={`radar-open-${item.doc_id}`}>
        <div className="flex items-center gap-2">
          <p className="font-heading font-bold text-sm text-onSurface truncate">{item.client_name}</p>
          {item.chased_recently && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-surfaceTertiary text-muted-foreground shrink-0">Chased</span>}
        </div>
        <p className={`text-xs font-semibold truncate ${toneClass}`}>{money(item.amount)} · {s.text}</p>
        <p className="text-[11px] text-muted-foreground truncate">{item.number}{!isInvoice && item.title ? ` · ${item.title}` : ""}</p>
      </button>
      <button data-testid={`radar-chase-${item.doc_id}`} onClick={() => onChase(item.doc_id)} className="shrink-0 bg-brandPrimary text-onBrand rounded-full px-4 h-10 text-sm font-heading font-bold flex items-center gap-1.5 active:scale-95 transition-transform">
        <MessageSquare size={15} /> Chase
      </button>
    </div>
  );
};

export const MoneyRadar = ({ onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chaseId, setChaseId] = useState(null);
  const [actionItem, setActionItem] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/money-radar").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  if (loading) return <div data-testid="radar-loading" className="mt-5 h-40 bg-surfaceTertiary rounded-[20px] animate-pulse" />;
  if (!data) return null;

  const items = [...(data.attention || []), ...(data.quotes_awaiting || [])];
  const nothing = data.outstanding_count === 0 && data.quotes_awaiting_count === 0;

  const afterChange = () => { setLoading(true); load(); onChanged?.(); };

  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/documents/${actionItem.doc_id}`, { status }); toast.success(status === "paid" ? "Marked as paid" : `Marked ${status}`); setActionItem(null); afterChange(); }
    catch { toast.error("Couldn't update"); } finally { setBusy(false); }
  };

  const deleteDoc = async () => {
    setBusy(true);
    try { await api.delete(`/documents/${confirmItem.doc_id}`); toast.success("Document deleted"); setConfirmItem(null); afterChange(); }
    catch { toast.error("Couldn't delete"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5" data-testid="money-radar">
      <div className="bg-onSurface text-onBrand rounded-[20px] p-5 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/5" />
        <div className="flex items-center gap-2 mb-4 relative">
          <Radar size={18} className="text-brandPrimary" />
          <p className="font-heading font-extrabold text-sm tracking-wide">Money Radar</p>
        </div>
        <div className="grid grid-cols-2 gap-4 relative">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/60">Outstanding</p>
            <p data-testid="radar-outstanding" className="font-heading text-3xl font-extrabold mt-0.5">{money(data.outstanding_total)}</p>
            <p className="text-[11px] text-white/60 mt-0.5">{data.outstanding_count} invoice{data.outstanding_count === 1 ? "" : "s"} · payment not recorded</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/60 flex items-center gap-1"><TriangleAlert size={12} className="text-brandPrimary" /> Overdue</p>
            <p data-testid="radar-overdue" className="font-heading text-3xl font-extrabold mt-0.5 text-brandPrimary">{money(data.overdue_total)}</p>
            <p className="text-[11px] text-white/60 mt-0.5">{data.overdue_count} need{data.overdue_count === 1 ? "s" : ""} chasing</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 relative">
          <span data-testid="radar-awaiting" className="text-[11px] font-semibold bg-white/10 rounded-full px-2.5 py-1">{data.quotes_awaiting_count} quote{data.quotes_awaiting_count === 1 ? "" : "s"} awaiting reply</span>
          {data.missed_invoice_count > 0 && <span data-testid="radar-missed" className="text-[11px] font-semibold bg-brandPrimary/25 text-white rounded-full px-2.5 py-1">{data.missed_invoice_count} possibly missed invoice{data.missed_invoice_count === 1 ? "" : "s"}</span>}
        </div>
      </div>

      {nothing ? (
        <div data-testid="radar-clear" className="mt-3 bg-surface border border-border rounded-[16px] p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brandTertiary flex items-center justify-center"><CheckCircle2 size={20} className="text-success" /></div>
          <div>
            <p className="font-heading font-bold text-onSurface text-sm">All clear</p>
            <p className="text-xs text-muted-foreground">No outstanding invoices or open quotes right now.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mt-4 mb-2">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Needs attention</p>
          </div>
          <div className="space-y-2">
            {items.slice(0, 6).map((it) => <Row key={it.doc_id} item={it} onChase={setChaseId} onOpen={setActionItem} />)}
          </div>
        </>
      )}

      {chaseId && <ChaseSheet docId={chaseId} onClose={() => setChaseId(null)} onDone={afterChange} />}

      <BottomSheet open={!!actionItem} onClose={() => setActionItem(null)} title={actionItem?.client_name || ""} testid="radar-action-sheet">
        {actionItem && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground -mt-1 mb-1">{actionItem.number} · {money(actionItem.amount)}{actionItem.kind === "invoice" ? "" : ` · ${actionItem.title || "Quote"}`}</p>
            {actionItem.kind === "invoice" && (
              <button data-testid="radar-action-paid" disabled={busy} onClick={() => setStatus("paid")} className="w-full bg-success text-white rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"><CheckCircle2 size={18} /> Mark as paid</button>
            )}
            <button data-testid="radar-action-chase" disabled={busy} onClick={() => { const id = actionItem.doc_id; setActionItem(null); setChaseId(id); }} className="w-full bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"><MessageSquare size={17} /> Chase for payment</button>
            <button data-testid="radar-action-open" disabled={busy} onClick={() => navigate(`/doc/${actionItem.doc_id}`)} className="w-full bg-surface border border-border text-onSurface rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"><Eye size={17} /> Open document</button>
            <button data-testid="radar-action-delete" disabled={busy} onClick={() => { const it = actionItem; setActionItem(null); setConfirmItem(it); }} className="w-full bg-surface border border-border text-error rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"><Trash2 size={17} /> Delete document</button>
          </div>
        )}
      </BottomSheet>

      <ConfirmSheet
        open={!!confirmItem}
        onClose={() => setConfirmItem(null)}
        onConfirm={deleteDoc}
        busy={busy}
        title="Delete this document?"
        message={confirmItem ? `${confirmItem.number} · ${confirmItem.client_name} will be permanently deleted. This can't be undone.` : ""}
        testid="radar-delete-confirm"
      />
    </div>
  );
};
